import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import {
  getPublicSpinWheel,
  getMySpinWheel,
  SpinWheel,
} from '../../api/payment/spin-wheel.api';

/**
 * Overlay VÒNG QUAY — chèn vào OBS bằng Browser Source, chạy không cần đăng nhập.
 *
 * Bình thường trong suốt hoàn toàn. Khi có người quay (sự kiện SignalR ReceiveSpinResult)
 * thì "game tự show lên": đĩa quay rồi dừng ở ô trúng, công bố nhiệm vụ, sau đó tự ẩn.
 *
 * Lưu ý công bằng: KẾT QUẢ do server quyết, đĩa quay chỉ là hình ảnh. Khi streamer BẬT
 * hiện tỉ lệ thì vẽ các múi ĐÚNG theo trọng số; khi TẮT thì vẽ múi đều nhau (vì BE cố ý
 * không trả trọng số ra ngoài) — không vẽ múi đều trong khi tỉ lệ thật lệch nếu đang hiện tỉ lệ.
 */
const COLORS = [
  '#39539E', '#E4572E', '#17A398', '#F4A259',
  '#7B2CBF', '#2A9D8F', '#D62828', '#457B9D',
  '#8AC926', '#FF70A6', '#3A86FF', '#FFBE0B',
];

const PREVIEW_EVERY_MS = 12000; // chế độ xem thử: tự quay lại sau mỗi chừng này
const SHOW_MS = 9000;   // tổng thời gian hiện trên stream
const SPIN_MS = 4200;   // thời gian đĩa quay
const SPIN_START_DELAY_MS = 80; // đợi đĩa vẽ xong rồi mới đổi góc (xem ghi chú trong playSpin)

const OUTLINE =
  '0 0 3px rgba(0,0,0,.95), 0 2px 6px rgba(0,0,0,.85), 0 0 14px rgba(0,0,0,.6)';

const PageSpinWheelLivestream = () => {
  const { contentCreatorId } = useParams<{ contentCreatorId: string }>();
  const [searchParams] = useSearchParams();
  // Chế độ XEM THỬ: streamer mở từ trang cài đặt để căn vị trí trong OBS trước khi lên sóng.
  // Luôn hiện + tự quay lặp lại, không cần chờ ai quay thật.
  const isTest = searchParams.get('isTest') === 'true';
  const [wheel, setWheel] = useState<SpinWheel | null>(null);
  const [visible, setVisible] = useState(false);
  const [angle, setAngle] = useState(0);
  const [result, setResult] = useState<{ spinner: string; label: string } | null>(null);
  const [revealed, setRevealed] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const load = useCallback(async () => {
    if (!contentCreatorId) return null;
    // Xem thử: ưu tiên cấu hình CỦA CHÍNH MÌNH (streamer đang đăng nhập) để vẫn xem thử
    // được khi vòng quay đang TẮT — lúc đó endpoint công khai cố ý không trả gì.
    if (isTest) {
      try {
        const mine = await getMySpinWheel();
        if (mine?.exists && mine.items?.length) {
          setWheel(mine);
          return mine;
        }
      } catch {
        /* không đăng nhập -> rơi xuống bản công khai */
      }
    }
    try {
      const w = await getPublicSpinWheel(contentCreatorId);
      setWheel(w);
      return w;
    } catch {
      return null;
    }
  }, [contentCreatorId, isTest]);

  useEffect(() => {
    load();
  }, [load]);

  /** Chạy hiệu ứng quay rồi công bố kết quả. Dùng chung cho quay thật và xem thử. */
  const playSpin = useCallback(
    (items: { label: string }[], spinner: string, label: string) => {
      const idx = Math.max(0, items.findIndex((i) => i.label === label));

      clearTimers();
      setResult({ spinner, label });
      setRevealed(false);
      setVisible(true);

      // Quay nhiều vòng rồi dừng giữa múi trúng.
      const n = items.length || 1;
      const seg = 360 / n;
      const target = 360 * 6 + (360 - (idx * seg + seg / 2));

      // QUAN TRỌNG: đĩa chỉ được VẼ khi overlay hiện. Nếu đặt góc mới ngay trong cùng
      // lần cập nhật này thì React gắn đĩa thẳng ở góc CUỐI -> CSS không có mốc đầu để
      // chạy transition, đĩa đứng im và nhìn như "ra kết quả luôn". Phải đợi đĩa vẽ
      // xong ở góc cũ rồi mới đổi góc thì mới thấy nó quay.
      timers.current.push(
        setTimeout(() => setAngle((prev) => prev + target), SPIN_START_DELAY_MS)
      );

      timers.current.push(
        setTimeout(() => setRevealed(true), SPIN_START_DELAY_MS + SPIN_MS)
      );
      // Xem thử thì GIỮ NGUYÊN trên màn hình để streamer căn vị trí trong OBS.
      if (!isTest) {
        timers.current.push(
          setTimeout(() => setVisible(false), SPIN_START_DELAY_MS + SHOW_MS)
        );
      }
    },
    [isTest]
  );

  // ── Chế độ xem thử: tự quay lặp lại bằng chính danh sách nhiệm vụ đang cấu hình ──
  useEffect(() => {
    if (!isTest) return undefined;
    let stopped = false;

    const demo = async () => {
      const w = (await load()) || wheel;
      const items = w?.items || [];
      if (stopped || items.length === 0) return;
      const pick = items[Math.floor(Math.random() * items.length)];
      playSpin(items, 'Xem thử', pick.label);
    };

    demo();
    const timer = setInterval(demo, PREVIEW_EVERY_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTest, load, playSpin]);

  useEffect(() => {
    if (!contentCreatorId || isTest) return undefined;  // xem thử thì không cần hub
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection
      .start()
      .then(() => {
        connection.on('ReceiveSpinResult', async (data: any) => {
          // Tải lại danh sách ô phòng khi streamer vừa sửa giữa phiên.
          const w = (await load()) || wheel;
          playSpin(w?.items || [], data?.spinnerUserName || '', data?.itemLabel || '');
        });
      })
      .catch(() => {
        /* không nối được hub -> overlay đứng im, không vỡ trang */
      });

    return () => {
      clearTimers();
      connection.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentCreatorId, isTest, load, playSpin]);

  if (!visible || !result) return null;

  const items = wheel?.items || [];
  const n = items.length || 1;
  // Múi theo trọng số khi streamer hiện tỉ lệ; ngược lại chia đều (BE không trả trọng số).
  const hasWeights = items.every((i) => typeof i.weight === 'number');
  const totalW = hasWeights ? items.reduce((s, i) => s + (i.weight || 0), 0) : 0;

  let acc = 0;
  const stops = items
    .map((it, i) => {
      const share = hasWeights && totalW > 0 ? ((it.weight || 0) / totalW) * 360 : 360 / n;
      const from = acc;
      acc += share;
      return `${COLORS[i % COLORS.length]} ${from}deg ${acc}deg`;
    })
    .join(', ');

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-transparent p-4">
      <div className="relative">
        {/* Kim chỉ ở đỉnh */}
        <div
          className="absolute left-1/2 top-[-14px] z-10 h-0 w-0 -translate-x-1/2"
          style={{
            borderLeft: '14px solid transparent',
            borderRight: '14px solid transparent',
            borderTop: '24px solid #fff',
            filter: 'drop-shadow(0 2px 3px rgba(0,0,0,.8))',
          }}
        />
        <div
          className="h-[260px] w-[260px] rounded-full ring-4 ring-white/80"
          style={{
            background: stops ? `conic-gradient(${stops})` : '#39539E',
            transform: `rotate(${angle}deg)`,
            transition: `transform ${SPIN_MS}ms cubic-bezier(.17,.67,.21,1)`,
            boxShadow: '0 8px 30px rgba(0,0,0,.45)',
          }}
        />
      </div>

      <div className="max-w-[640px] text-center">
        <div
          className="text-2xl font-extrabold text-white"
          style={{ textShadow: OUTLINE }}
        >
          🎡 {result.spinner} vừa quay
        </div>
        {revealed && (
          <div
            className="mt-1 text-4xl font-extrabold text-yellow-300"
            style={{ textShadow: OUTLINE }}
          >
            {result.label}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageSpinWheelLivestream;
